import { frameId } from "../assets/manifest.ts";
import { COMPILED_COLLISION_CELLS_PER_TILE, type PropInstance, type WorldDocument } from "../document/types.ts";
import { frameSizeOf, stateFootprint } from "./behaviour.ts";
import type { CompileContext } from "./context.ts";
import { footprintBlocks, footprintCorner, minCoverFor, type PropFootprint } from "./footprint.ts";
import { blockingFootprint } from "./prop-footprint.ts";

/**
 * The audit of where collision is laid (ADR-040 amendment of 2026-10-04, P1.6-T83). The rule: a prop's
 * collision is the footprint panel's cells (`footprintBlocks`) laid from the cell corner nearest the corner of
 * its frame as it is drawn, the bottom-left, or the bottom-right when mirrored, a tie going right and down.
 * This module states that rule from a prop's position alone (`footprintCorner`) and without stamping anything, so a
 * tool can hold the compile, the editor's overlay and the game to it, and names how far each prop's art stands
 * from the cells its collision takes, which is zero only for a prop whose corner is on a cell corner. A
 * thing's state frame is drawn centred under the placed frame, so its left edge is the prop's moved by half
 * the difference of the two widths.
 */

/**
 * One blocking prop as the rule lays it: where its frame is drawn (left and bottom edges in pixels, and its
 * width), the pixels from its art to its collision on each axis (negative left or up, at most half a cell),
 * and the world collision cells it blocks. `state` is the state for a behaviour prop, which has an entry for
 * each state that blocks (a prop driven by presence only for its initial state, the one its chunk carries),
 * and null for a plain prop. `kept` is true for a prop the author keeps as placed (`PropInstance.keepPlacement`), which
 * the align command never moves and the warning therefore never counts, and absent for any other.
 */
export interface LaidEntry {
  readonly mapId: string;
  readonly layerId: string;
  readonly propId: string;
  readonly state: string | null;
  readonly left: number;
  readonly bottom: number;
  readonly width: number;
  readonly flipX: boolean;
  readonly dx: number;
  readonly dy: number;
  readonly cells: readonly (readonly [number, number])[];
  readonly kept?: true;
}

/**
 * How many props the compile's warning names before it counts the rest.
 */
const NAMED = 8;

/**
 * One frame laid by the rule: from the corner `footprintCorner` gives, the one the stamp and the align command
 * (`cellOffset`, P1.6-T82) read, so the audit, the stamp and the Issues tab measure a prop the same way.
 *
 * @param footprint The frame's footprint.
 * @param left The drawn frame's left edge in pixels.
 * @param bottom The drawn frame's bottom edge in pixels.
 * @param flipX Whether the frame is mirrored.
 * @param flipY Whether the frame is mirrored top to bottom, its cells then mirrored inside the frame as the stamp
 *   mirrors them (ADR-040 amendment of 2026-10-07).
 * @param cellPixels Pixels per collision cell.
 * @param minCover The least cover that marks a cell at that cell size.
 * @returns The pixels from the art to the collision and the world cells, or null when the footprint blocks no
 *   cell.
 */
function laid(footprint: PropFootprint, left: number, bottom: number, flipX: boolean, flipY: boolean, cellPixels: number, minCover: number): Pick<LaidEntry, "dx" | "dy" | "cells"> | null {
  const blocks = footprintBlocks(footprint, cellPixels, flipY, minCover);
  if (blocks.length === 0) {
    return null;
  }
  const corner = footprintCorner(left, bottom, footprint.w, flipX, cellPixels);
  const column = Math.round((flipX ? corner.left + footprint.w : corner.left) / cellPixels);
  const row = Math.round(corner.bottom / cellPixels);
  return { dx: corner.left - left, dy: corner.bottom - bottom, cells: blocks.map(([c, up]) => [flipX ? column - 1 - c : column + c, row - 1 - up] as const) };
}

/**
 * The entries of one prop.
 *
 * @param prop The prop.
 * @param context The compile context.
 * @param tileSize Tile size in pixels.
 * @param cellsPerTile Collision cells per tile of the grid the cells are counted in.
 * @returns Its state, drawn frame and laid cells for each footprint it can block with.
 */
function entriesOf(prop: PropInstance, context: CompileContext, tileSize: number, cellsPerTile: number): Omit<LaidEntry, "mapId" | "layerId" | "propId">[] {
  const cellPixels = tileSize / cellsPerTile;
  const minCover = minCoverFor(cellsPerTile);
  const bottom = (prop.y + 1) * tileSize;
  if (prop.behaviour === undefined) {
    const footprint = blockingFootprint(prop, context, tileSize);
    const cells = footprint === undefined ? null : laid(footprint, prop.x * tileSize, bottom, prop.flipX, prop.flipY, cellPixels, minCover);
    return footprint === undefined || cells === null ? [] : [{ state: null, left: prop.x * tileSize, bottom, width: footprint.w, flipX: prop.flipX, ...cells }];
  }
  const type = context.objectTypes.find((t) => t.type === prop.behaviour?.type);
  if (type === undefined) {
    return [];
  }
  const out: Omit<LaidEntry, "mapId" | "layerId" | "propId">[] = [];
  const placedWidth = frameSizeOf(context, frameId(prop.frame.tileset, null, prop.frame.tile))?.w;
  for (const state of type.inside === undefined ? type.states : type.states.filter((s) => s.id === type.initial)) {
    const frame = state.id === type.initial ? prop.frame : prop.behaviour.frames?.[state.id];
    if (frame === undefined || (typeof state.footprint !== "string" && frameSizeOf(context, frameId(frame.tileset, null, frame.tile)) === undefined)) {
      continue;
    }
    const footprint = stateFootprint(prop, context, tileSize, state.id);
    if (footprint === null) {
      continue;
    }
    const left = prop.x * tileSize + (type.inside !== undefined || placedWidth === undefined ? 0 : (placedWidth - footprint.w) / 2);
    const cells = laid(footprint, left, bottom, prop.flipX, prop.flipY, cellPixels, minCover);
    if (cells !== null) {
      out.push({ state: state.id, left, bottom, width: footprint.w, flipX: prop.flipX, ...cells });
    }
  }
  return out;
}

/**
 * Every blocking prop of a world as the rule lays it, in document order. Only the footprints rule lays a
 * footprint, so a context without one audits nothing.
 *
 * @param world The world document.
 * @param context The compile context, for the footprints, the object types and the frame sizes.
 * @param cellsPerTile Collision cells per tile the cells are counted in: the compiled grid's by default, the
 *   authoring cell's (`COLLISION_CELLS_PER_TILE`) for the paths that still work in authoring cells (the footprint
 *   area, the activation cells).
 * @returns The entries.
 */
export function laidAudit(world: WorldDocument, context: CompileContext, cellsPerTile: number = COMPILED_COLLISION_CELLS_PER_TILE): LaidEntry[] {
  if (!context.collision.rules.some((rule) => rule.kind === "footprints")) {
    return [];
  }
  const out: LaidEntry[] = [];
  for (const map of world.maps) {
    for (const layer of map.layers) {
      if (layer.kind !== "prop") {
        continue;
      }
      for (const prop of layer.props) {
        for (const entry of entriesOf(prop, context, world.tileSize, cellsPerTile)) {
          out.push({ mapId: map.id, layerId: layer.id, propId: prop.id, ...entry, ...(prop.keepPlacement === true ? { kept: true as const } : {}) });
        }
      }
    }
  }
  return out;
}

/**
 * How far and which way a collision lies from its art, in words.
 *
 * @param dx Pixels across, negative left.
 * @param dy Pixels down, negative up.
 * @returns For example "3 px left and 4 px down".
 */
function away(dx: number, dy: number): string {
  const round = (value: number): number => Math.round(Math.abs(value) * 100) / 100;
  const parts = [...(dx === 0 ? [] : [`${round(dx)} px ${dx < 0 ? "left" : "right"}`]), ...(dy === 0 ? [] : [`${round(dy)} px ${dy < 0 ? "up" : "down"}`])];
  return parts.join(" and ");
}

/**
 * The compile's one warning for props whose collision cannot lie exactly under their art: how many of the
 * blocking props stand off the collision cells, the furthest any collision lies from its art, and the first
 * few by id with the way their collision lies from them. A behaviour prop is counted once, by the first of its
 * states that stands off. A prop kept as placed is never one that stands off, as the align command never moves it
 * (`prop-align.ts`), though it still counts among the blocking props the total is taken of.
 *
 * @param entries The audit.
 * @returns The warning, or null when every blocking prop stands on the cells or is kept as placed.
 */
export function offCellWarning(entries: readonly LaidEntry[]): string | null {
  const props = new Map<string, LaidEntry | null>();
  let furthest = 0;
  for (const entry of entries) {
    const key = `${entry.mapId}/${entry.layerId}/${entry.propId}`;
    const off = entry.kept !== true && (entry.dx !== 0 || entry.dy !== 0);
    if (off) {
      furthest = Math.max(furthest, Math.abs(entry.dx), Math.abs(entry.dy));
    }
    if (!props.has(key) || (off && props.get(key) === null)) {
      props.set(key, off ? entry : null);
    }
  }
  const standingOff = [...props.values()].filter((entry): entry is LaidEntry => entry !== null);
  if (standingOff.length === 0) {
    return null;
  }
  const one = standingOff.length === 1;
  const named = standingOff.slice(0, NAMED).map((entry) => `${entry.propId} ${away(entry.dx, entry.dy)}`).join(", ");
  const rest = standingOff.length > NAMED ? ` and ${standingOff.length - NAMED} more` : "";
  return `${standingOff.length} of ${props.size} blocking props ${one ? "stands" : "stand"} off the collision cells, so ${one ? "its" : "their"} collision lies up to ${Math.round(furthest * 100) / 100} px from ${one ? "its" : "their"} art (the footprint panel's cells, laid from the nearest cell corner): ${named}${rest}. Align props to collision cells in the Edit menu moves the art onto its collision, with the pieces it is composed with; Align alone on a prop's line in the Issues tab moves that prop alone`;
}
